import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/customSupabaseClient';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { toast } from 'sonner';

/**
 * Hook for bookmarking exercises on behalf of an athlete.
 * Admins search all users via search_users_with_activity;
 * support staff see only their assigned athletes via staff_get_assigned_users.
 *
 * Separate from useExerciseBookmarks, which manages the signed-in coach's own list.
 */
export const useAthleteBookmark = () => {
  const { isAdmin, isSupport } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [athleteSearchLoading, setAthleteSearchLoading] = useState(false);
  const [athleteBookmarks, setAthleteBookmarks] = useState(new Set());
  const [bookmarksLoading, setBookmarksLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const assignedCacheRef = useRef(null);

  const searchAthletes = useCallback(async (term) => {
    const trimmed = (term || '').trim();

    if (isAdmin) {
      if (trimmed.length < 2) {
        setAthletes([]);
        return;
      }
      setAthleteSearchLoading(true);
      try {
        const { data, error } = await supabase.rpc('search_users_with_activity', {
          p_search_term: trimmed,
          p_limit: 15,
        });
        if (error) throw error;
        setAthletes(data || []);
      } catch (err) {
        console.error('Error searching athletes:', err);
        setAthletes([]);
      } finally {
        setAthleteSearchLoading(false);
      }
    } else if (isSupport) {
      if (!assignedCacheRef.current) {
        setAthleteSearchLoading(true);
        try {
          const { data, error } = await supabase.rpc('staff_get_assigned_users');
          if (error) throw error;
          assignedCacheRef.current = data || [];
        } catch (err) {
          console.error('Error fetching assigned users:', err);
          assignedCacheRef.current = [];
        } finally {
          setAthleteSearchLoading(false);
        }
      }
      if (trimmed.length < 2) {
        setAthletes(assignedCacheRef.current);
        return;
      }
      const lower = trimmed.toLowerCase();
      setAthletes(
        assignedCacheRef.current.filter(
          (u) =>
            (u.full_name || '').toLowerCase().includes(lower) ||
            (u.email || '').toLowerCase().includes(lower),
        ),
      );
    }
  }, [isAdmin, isSupport]);

  const fetchAthleteBookmarks = useCallback(async (athleteId) => {
    if (!athleteId) {
      setAthleteBookmarks(new Set());
      return;
    }
    setBookmarksLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_exercise_bookmarks')
        .select('exercise_id')
        .eq('user_id', athleteId);
      if (error) throw error;
      setAthleteBookmarks(new Set((data || []).map((r) => r.exercise_id)));
    } catch (err) {
      console.error('Error fetching athlete bookmarks:', err);
      setAthleteBookmarks(new Set());
    } finally {
      setBookmarksLoading(false);
    }
  }, []);

  const addBookmark = useCallback(async (athleteId, exerciseId) => {
    setBusy(true);
    try {
      const { error } = await supabase
        .from('user_exercise_bookmarks')
        .insert({ user_id: athleteId, exercise_id: exerciseId });
      if (error) {
        if (error.code === '23505') {
          toast.info('Already bookmarked for this athlete');
          setAthleteBookmarks((prev) => new Set(prev).add(exerciseId));
          return true;
        }
        if (error.code === '42501') {
          toast.error("You're not assigned to this athlete");
          return false;
        }
        throw error;
      }
      setAthleteBookmarks((prev) => new Set(prev).add(exerciseId));
      toast.success('Bookmarked for athlete');
      return true;
    } catch (err) {
      console.error('Error adding bookmark for athlete:', err);
      toast.error('Could not bookmark exercise', { description: err.message });
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const removeBookmark = useCallback(async (athleteId, exerciseId) => {
    setBusy(true);
    try {
      const { error } = await supabase
        .from('user_exercise_bookmarks')
        .delete()
        .eq('user_id', athleteId)
        .eq('exercise_id', exerciseId);
      if (error) {
        if (error.code === '42501') {
          toast.error("You're not assigned to this athlete");
          return false;
        }
        throw error;
      }
      setAthleteBookmarks((prev) => {
        const next = new Set(prev);
        next.delete(exerciseId);
        return next;
      });
      toast.success('Bookmark removed for athlete');
      return true;
    } catch (err) {
      console.error('Error removing bookmark for athlete:', err);
      toast.error('Could not remove bookmark', { description: err.message });
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const isAthleteBookmarked = useCallback(
    (exerciseId) => athleteBookmarks.has(exerciseId),
    [athleteBookmarks],
  );

  return {
    athletes,
    athleteSearchLoading,
    searchAthletes,
    athleteBookmarks,
    bookmarksLoading,
    fetchAthleteBookmarks,
    isAthleteBookmarked,
    addBookmark,
    removeBookmark,
    busy,
  };
};
